/**
 * Chakra palette registration — TIER 2 of the token model.
 *
 * This file used to hold fifteen hand-maintained ramps (`brand`, `accent`,
 * `purple`, `secondary`, `copper`, `pink`, `gray`, `green`, `red`, `yellow`,
 * `orange`, `blue`, `brown`, …). Three of them were duplicates of the SAME
 * identity colour and one had gone stale: `brand.700` still held #2E446D after
 * the ramp was corrected to #3F5C94, so tokens pointing at it silently
 * disagreed with the transcribed palette. The contrast suite never caught it,
 * because the stale values were themselves AA-compliant — correct and wrong.
 *
 * There is now exactly one source per colour:
 *
 *   colors.brand.ts  raw ramps, transcribed from the guidelines (brand-named)
 *   roles.ts         which ramp does which JOB (the rename seam)
 *   this file        registers those roles as Chakra palette keys
 *   semanticTokens.ts  role -> light/dark pairs the components consume
 *
 * Components and semantic tokens reference ROLE names only (`accent.600`,
 * `neutral.400`, `danger.700`). Renaming Twilight to something else is a
 * one-line edit in roles.ts and touches nothing here.
 *
 * `ui` and `ink` remain as named one-off surfaces rather than ramps; both are
 * expressed in role terms so they move with a rebrand too.
 */

import { ROLES } from './roles';

const { neutral, accent } = ROLES;

export const colors = {
  ...ROLES,

  // Named chrome surfaces. Not a ramp — these are specific, single-purpose
  // values, kept separate so a semantic token can say what it means.
  ui: {
    background: neutral[100],
    backgroundAlt: '#FFFFFF',
    card: '#FFFFFF',
    border: neutral[300],
    sidebar: '#E9E7E1',
    sidebarBorder: neutral[300],
    sidebarAccent: '#E0DDD6',
    sidebarActive: '#D5D2CA',
  },

  // Body ink.
  //
  // NAMED `ink`, NOT `text`, DELIBERATELY — do not rename it back. Chakra
  // flattens raw palette paths AND `semanticTokens.colors` keys into one
  // `--chakra-colors-*` namespace, and the semantic token wins. A palette group
  // called `text` therefore collides with the `text.*` semantic tokens and
  // emits a circular `var()` that CSS resolves to EMPTY — invisible for
  // `color`, visible for `bg`/`borderColor`/`fill`.
  // `__tests__/semanticTokenResolution.test.ts` fails if any palette path ever
  // collides with a semantic token key again.
  ink: {
    primary: neutral[900],
  },

  // Focus/selection glow, expressed against the accent role.
  glow: {
    accent: accent[500],
  },
};
